import type { BookingTrackingCallbacks, BookingTrackingEvent, ScreeningProgram } from "@internal/telemedicine"
import { v4 as uuid } from "uuid"
import { Paths } from "#/utils/Paths"
import { MemberActions } from "./memberActions"
import type { MemberActionTaken } from "./useMemberActionTracking"

export type BookingEntrySource = "homepage_card" | "appointments_page" | "carrot_plan" | "provider_finder" | "direct"
export type PreparationSource = "booking_confirmation" | "homepage_card" | "carrot_plan"
type AppointmentType = Parameters<BookingTrackingCallbacks["onBookingSubmitted"]>[0]
type Metadata = Record<string, string | number>

/** In-memory correlation for the active booking journey. Nothing is persisted in browser or router state. */
export function createBookingTrackingStore(
  record: (action: MemberActionTaken) => void,
  createId: () => string = () => uuid()
) {
  const recordAction = (action: MemberActionTaken) => {
    try {
      record(action)
    } catch {
      // Telemetry must not prevent navigation or change a successful booking into a UI failure.
    }
  }
  function createFlow(entrySource: BookingEntrySource, program?: ScreeningProgram) {
    const bookingFlowId = createId()
    let flowProgram = program
    let appointmentType: AppointmentType | undefined
    let eventSequence = 0
    let bookingAttemptNumber = 0
    let currentScreen: string | undefined
    let confirmedAppointmentId: string | undefined
    let emptyAvailability: string | undefined
    let consentSkipped = false
    let gatingSkipped = false
    const initialized = new Set<string>()
    const confirmedAttempts = new Set<number>()

    const emit = (memberActionTaken: MemberActions, metadata: Metadata = {}) => {
      recordAction({
        memberActionTaken,
        metaData: {
          bookingFlowId,
          eventSequence: ++eventSequence,
          ...(flowProgram ? { program: flowProgram } : {}),
          ...(appointmentType ? { appointmentType } : {}),
          ...metadata
        }
      })
    }
    const skipConsent = () => {
      if (consentSkipped) return
      consentSkipped = true
      emit(MemberActions.BOOKING_CONSENT_SKIPPED)
    }
    const skipGating = (resolvedType: AppointmentType) => {
      if (gatingSkipped) return
      gatingSkipped = true
      appointmentType = resolvedType
      emit(MemberActions.BOOKING_GATING_SKIPPED)
      if (resolvedType === "follow_up") {
        emit(MemberActions.BOOKING_GATING_RESULT, { result: "eligible", resultSource: "follow_up" })
      }
    }
    const markScreen = (key: string) => {
      if (currentScreen === key) return false
      currentScreen = key
      emptyAvailability = undefined
      return true
    }
    const onEvent = (event: BookingTrackingEvent) => {
      switch (event.type) {
        case "initialized": {
          appointmentType = event.appointmentType
          const key = `${event.appointmentType}:${event.ineligible}`
          if (initialized.has(key)) return
          initialized.add(key)
          if (event.ineligible) {
            emit(MemberActions.BOOKING_GATING_RESULT, { result: "ineligible", resultSource: "known_data" })
          } else if (event.consentSkipped) {
            skipConsent()
            if (event.noQuestions) skipGating(event.appointmentType)
          }
          return
        }
        case "screen": {
          const view = event.view
          const key =
            view.screen === "consent"
              ? `consent:${view.consentStep}`
              : view.screen === "scheduling"
              ? `scheduling:${view.appointmentType}`
              : view.screen
          if (!markScreen(key)) return
          if (view.screen === "consent") emit(MemberActions.BOOKING_CONSENT_VIEWED, { consentStep: view.consentStep })
          else if (view.screen === "gating")
            emit(MemberActions.BOOKING_GATING_VIEWED, { gatingStep: "health_questions" })
          else if (view.screen === "scheduling") {
            appointmentType = view.appointmentType
            emit(MemberActions.BOOKING_SCHEDULING_VIEWED)
          }
          return
        }
        case "consent_skipped":
          skipConsent()
          return
        case "gating_skipped":
          skipGating(event.appointmentType)
          return
        case "gating_result":
          emit(MemberActions.BOOKING_GATING_RESULT, { result: event.result, resultSource: event.resultSource })
          return
        case "needs_consent":
          consentSkipped = false
          gatingSkipped = false
          return
        case "availability": {
          if (event.outcome === null) {
            emptyAvailability = undefined
            return
          }
          const key = `${event.searchKey}:${event.outcome}`
          if (emptyAvailability === key) return
          emptyAvailability = key
          emit(MemberActions.BOOKING_NO_DATES, { availabilityOutcome: event.outcome })
          return
        }
        case "success":
          if (confirmedAttempts.has(event.bookingAttemptNumber)) return
          confirmedAttempts.add(event.bookingAttemptNumber)
          confirmedAppointmentId = event.appointmentId
          emit(MemberActions.BOOKING_SUCCESS, {
            appointmentId: event.appointmentId,
            hostId: event.hostId,
            bookingAttemptNumber: event.bookingAttemptNumber
          })
          return
        case "prepare":
          emit(MemberActions.BOOKING_PREPARE_FOR_VISIT_CLICKED, {
            appointmentId: event.appointmentId,
            source: "booking_confirmation"
          })
      }
    }
    const callbacks: BookingTrackingCallbacks = {
      onEvent,
      onBookingSubmitted: (resolvedType) => {
        appointmentType = resolvedType
        emit(MemberActions.BOOKING_SUBMITTED, { bookingAttemptNumber: ++bookingAttemptNumber })
        return bookingAttemptNumber
      }
    }
    emit(MemberActions.BOOKING_FLOW_ENTERED, { entrySource })
    return {
      bookingFlowId,
      callbacks,
      getProgram: () => flowProgram,
      isOnForms: () => currentScreen === "forms",
      continueToForms: (pathname: string) => {
        if (
          !confirmedAppointmentId ||
          (currentScreen !== "booked" && currentScreen !== "forms") ||
          pathname !== `${Paths.TELEMEDICINE_BOOKING}/${confirmedAppointmentId}/forms`
        )
          return false
        markScreen("forms")
        return true
      },
      bindProgram: (value: ScreeningProgram) => {
        flowProgram = value
      },
      returnToFinder: () => {
        if (!markScreen("provider_finder")) return
        // A real return through Finder runs the gates again. Filter changes there do not.
        initialized.clear()
        consentSkipped = false
        gatingSkipped = false
      }
    }
  }

  let active: ReturnType<typeof createFlow> | undefined
  const start = (source: BookingEntrySource, program?: ScreeningProgram) => {
    active = createFlow(source, program)
    return active.bookingFlowId
  }
  const selectProgram = (program: ScreeningProgram, source: BookingEntrySource = "direct") => {
    if (!active || (active.getProgram() && active.getProgram() !== program)) active = createFlow(source, program)
    active.bindProgram(program)
    return active
  }
  return {
    start,
    selectProgram,
    end: () => {
      active = undefined
    },
    navigate: (pathname: string) => {
      if (active?.continueToForms(pathname)) return
      // Leaving forms ends this journey, even when Back returns to booking or Provider Finder.
      if (active?.isOnForms()) active = undefined
      if (pathname === Paths.PROVIDER_FINDER || pathname.startsWith(`${Paths.PROVIDER_FINDER}/`))
        active?.returnToFinder()
      else if (pathname !== Paths.TELEMEDICINE && pathname !== Paths.TELEMEDICINE_BOOKING) active = undefined
    },
    prepare: (appointmentId: string, source: Exclude<PreparationSource, "booking_confirmation">) =>
      recordAction({
        memberActionTaken: MemberActions.BOOKING_PREPARE_FOR_VISIT_CLICKED,
        metaData: { appointmentId, source }
      })
  }
}
